import React, { useEffect, useState } from "react";
import { CalendarDays, MapPin, Video, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { endpoints } from "@/lib/api";

const statusTone = {
  Confirmed: "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/20",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/20",
  Completed: "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/20",
};

export default function TodaysSchedule({ refreshKey }) {
  const [items, setItems] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    endpoints.listAppointments().then((all) => {
      const list = all.filter((a) => a.date === today).sort((a, b) => a.time.localeCompare(b.time));
      setItems(list);
    }).catch(() => {});
  }, [refreshKey, today]);

  return (
    <div data-testid="todays-schedule-card" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-300 flex items-center justify-center">
          <CalendarDays className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Today&apos;s Schedule</h2>
          <p className="text-xs text-slate-500">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
        </div>
        <Badge variant="secondary" className="rounded-full">{items.length}</Badge>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
            <CalendarDays className="h-5 w-5 text-slate-400" />
          </div>
          Nothing scheduled today.<br />Add an appointment from Quick Actions.
        </div>
      ) : (
        <ol className="relative border-l border-slate-200 dark:border-white/10 ml-3 pl-5 space-y-4">
          {items.map((a) => (
            <li key={a.id} data-testid={`schedule-item-${a.id}`} className="relative">
              <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full brand-gradient ring-4 ring-white dark:ring-slate-900" />
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold">{a.time}</span>
                    <Badge variant="outline" className={`text-[10px] rounded-full ${statusTone[a.status] || ""}`}>{a.status}</Badge>
                  </div>
                  <div className="text-sm font-semibold mt-0.5 truncate">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                    {a.location?.startsWith("http") ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                    <span className="truncate">{a.client || a.location || a.type}</span>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-400 mt-1 shrink-0" />
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
